import { useState } from "react";
import { RefreshCw } from "lucide-react";
import useVersionCheck from "@/hooks/useVersionCheck";

/**
 * Two visual elements:
 *  - When the PWA bundle is outdated vs. the deployed one, displays a sticky banner
 *    inviting the user to refresh. This is mainly aimed at iOS Safari which caches
 *    PWAs aggressively.
 *  - A small footer-style version pill (passive) shown via `<VersionPill />` below.
 */
export default function VersionBanner() {
  const { current, latest, outdated } = useVersionCheck();
  const [dismissed, setDismissed] = useState(false);

  if (!outdated || dismissed) return null;

  const hardRefresh = () => {
    // Best-effort cache busting: query string + reload.
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("v", latest?.build_id || Date.now().toString());
      window.location.replace(url.toString());
    } catch {
      window.location.reload();
    }
  };

  return (
    <div
      data-testid="version-banner"
      className="fixed top-2 inset-x-2 z-[60] mx-auto max-w-2xl rounded-md shadow-lg border px-3 py-2 flex items-center gap-3"
      style={{ background: "#fff7ed", borderColor: "#fb923c", color: "#9a3412" }}
    >
      <RefreshCw className="w-4 h-4 shrink-0" />
      <div className="text-xs flex-1">
        <b>Nouvelle version disponible</b>
        <div className="text-[11px] opacity-80">
          Actuelle : <code>{current.build_id}</code> · Nouvelle : <code>{latest.build_id}</code>
        </div>
      </div>
      <button
        onClick={hardRefresh}
        data-testid="version-refresh-btn"
        className="text-xs font-semibold px-2 py-1 rounded border"
        style={{ borderColor: "#9a3412", background: "#fff" }}
      >
        Recharger
      </button>
      <button
        onClick={() => setDismissed(true)}
        data-testid="version-dismiss-btn"
        className="text-xs opacity-70 hover:opacity-100"
        aria-label="Ignorer"
      >
        ×
      </button>
    </div>
  );
}

export function VersionPill() {
  const { current } = useVersionCheck();
  return (
    <div
      data-testid="version-pill"
      className="text-[10px] text-stone-400 mt-2 text-center"
      title={current.built_at}
    >
      v{current.version}
    </div>
  );
}
